import BybAdaptiveModal from "@Components/common/byb-ds/drawers/byb-adaptive-modal";
import formatMetricScoreType from "@Containers/app/stat-cards/tooltips/format-metric-type";
import getMetricSuffix from "@Containers/app/stat-cards/tooltips/get-metric-suffix";
import { getLeagueInfo } from "@Containers/shared/helpers";
import { Actions, AppContext } from "@Context/common/general/context";
import ScoreFieldTypes from "@Enums/ScoreFieldTypes";
import { UserRankResult } from "@Globals/types";
import { formatRankWithOrdinal } from "@Helpers/format-utils";
import { formatName } from "@Helpers/formatters";
import useI18n from "@i18n";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import ChevronDownCircleIcon from "@Resources/app/icons-v2/system-icons/chevron-down-circle-icon";
import ChevronUpCircleIcon from "@Resources/app/icons-v2/system-icons/chevron-up-circle-icon";
import CrownIcon from "@Resources/app/icons-v2/system-icons/crown-icon";
import { isUndefined } from "lodash-es";
import { forwardRef, useContext, useState } from "react";
import BybButton from "../buttons/byb-button";
import BybButtonChip from "../buttons/byb-button-chip";
import BybAvatar from "../byb-avatar";
import BybHr from "../byb-hr";
import BybSectionHeader from "../sections/byb-section-header";
import BybListItem from "./byb-list-item";

interface IBybLeaderboardListItem {
  user?: UserRankResult;
  value?: number | null;
  metric?: string;
  rankChange?: "up" | "down" | "none";
  highlighted?: boolean;
  loading?: boolean;
  onClick?: () => void;
}

const BybLeaderboardListItem = forwardRef<HTMLDivElement, IBybLeaderboardListItem>(
  (
    {
      user,
      value,
      metric = ScoreFieldTypes.BYB_IQ,
      rankChange = "none",
      highlighted,
      loading,
      onClick,
    }: IBybLeaderboardListItem,
    ref
  ) => {
    const { i18n } = useI18n();
    const { dispatch } = useContext(AppContext);
    const theme = useTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [profileData, setProfileData] = useState<{ title: string; value?: string | number }[]>();
    const [loadingAdditionalData, setLoadingAdditionalData] = useState(true);
    const { doc_id, rating, rank, first, last, avatar } = user || {};
    const suffixLabel = getMetricSuffix(metric, Number(value));

    const getUserName = () => {
      if (!user) return "";
      return formatName(first, last);
    };

    const name = getUserName();

    const sticker =
      rankChange !== "none" ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {rankChange === "up" ? (
            <ChevronUpCircleIcon color={theme.palette.dsSuccess.v100} size={16} />
          ) : rankChange === "down" ? (
            <ChevronDownCircleIcon color={theme.palette.error.main} size={16} />
          ) : undefined}
        </Box>
      ) : undefined;

    const handleClick = () => {
      if (loading) {
        return;
      }

      onClick?.();
      setDrawerOpen(!drawerOpen);
      dispatch({
        type: Actions.ProgressionActions.GetProgressionConfig,
        cacheTime: [5, "minutes"],
        disableLoaders: true,
        onSuccess: (data) => {
          const leagueInfo = getLeagueInfo(data.leagues, rating || 0);

          setProfileData([
            { title: i18n("league.label"), value: i18n(`league.${leagueInfo.league?.name}.short`) },
            { title: i18n("division.label"), value: `#${leagueInfo.division?.name}` }
          ]);
          setLoadingAdditionalData(false);
        },
      });
    };

    if (!doc_id && !loading) {
      return (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", p: theme.spacing(2.75) }}>
          {i18n("leaderboard.user-rank-not-available")}
        </Typography>
      );
    }

    return (
      <>
        <Box ref={ref}>
          <BybListItem
            id={doc_id}
            iconType="avatar"
            listMarker={
              loading ? (
                <Skeleton variant="circular" width={16} height={16} />
              ) : rank && rank > 1 ? (
                rank
              ) : (
                <CrownIcon color={theme.palette.warning.light} size={16} />
              )
            }
            header={
              highlighted ? (
                <Typography variant="textBase600" color="dsBase.v200" textTransform={"capitalize"}>
                  {i18n("title.you")}
                </Typography>
              ) : (
                <Typography
                  variant="textBase400"
                  color="dsBase.v200"
                  textTransform={"capitalize"}
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  sx={{ overflow: "hidden" }}
                >
                  {name ? name : <Skeleton variant="text" width={theme.spacing(20)} />}
                </Typography>
              )
            }
            value={
              loading ? (
                <Skeleton variant="text" width={40} />
              ) : (
                <Typography variant="textBase600" color="dsBase.v200" whiteSpace="nowrap">
                  {value
                    ? `${formatMetricScoreType(metric, Number(value))} ${
                        suffixLabel ? i18n(suffixLabel) : ""
                      }`
                    : "-"}
                </Typography>
              )
            }
            bottomRightSticker={sticker}
            imgSrc={avatar || undefined}
            user={user}
            onClick={() => handleClick()}
          />
        </Box>

        <BybAdaptiveModal open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Stack spacing={4}>
            <Stack spacing={1.5} sx={{ alignItems: "center" }}>
              <BybAvatar src={user?.avatar || undefined} user={user} size={10} />
              <BybSectionHeader
                title={
                  <Typography
                    variant="text1xl700"
                    color="dsBase.v200"
                    sx={{ textTransform: "capitalize" }}
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {name}
                  </Typography>
                }
                subTitle={i18n("leaderboard.table-rank", {
                  rank: formatRankWithOrdinal(rank, i18n),
                })}
              />
              <Box sx={{ marginTop: theme.spacing(1) }}>
                <BybButtonChip>
                  {formatMetricScoreType(metric, Number(value))}{" "}
                  {suffixLabel ? i18n(suffixLabel) : ""}
                </BybButtonChip>
              </Box>
            </Stack>
            <BybHr />
            <Stack spacing={2}>
              {!profileData || loadingAdditionalData ? (
                <>
                  <Skeleton variant="text" height={theme.spacing(3)} />
                  <Skeleton variant="text" height={theme.spacing(3)} />
                  <Skeleton variant="text" height={theme.spacing(3)} />
                </>
              ) : (
                profileData.map((data, index) => (
                  <Stack key={index} direction="row" justifyContent="space-between">
                    <Typography variant="textBase500" color="dsBase.v200">
                      {data.title}
                    </Typography>
                    <Typography variant="textBase400" color="dsBase.v200">
                      {!isUndefined(data.value) ? data.value : "-"}
                    </Typography>
                  </Stack>
                ))
              )}
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: theme.spacing(2) }}>
              <BybButton size="small" color="secondary" onClick={() => setDrawerOpen(false)} fullWidth>
                {i18n("close")}
              </BybButton>

              {/* @davidrocha: Commenting this for now while until we support view other users' profiles feature */}
              {/*<BybButton size="small" color="primary" onClick={() => handleViewProfileClick(id)} fullWidth>
                {i18n("button.view.profile")}
              </BybButton>*/}
            </Box>
          </Stack>
        </BybAdaptiveModal>
      </>
    );
  }
);

export default BybLeaderboardListItem;
